

import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL;
const token = localStorage.getItem('accessToken');

export default function CharitySection({ caseId, sofaResponse }) {
  const [charityGifts, setCharityGifts] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [newCharity, setNewCharity] = useState({
    charity_name: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip_code: '',
    gift_description: '',
    transaction_date: '',
    transaction_value: ''
  });
  const [editCharity, setEditCharity] = useState(null);

  useEffect(() => {
    if (!sofaResponse) return;
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/cases/${caseId}/sofa-charity-gifts/?sofa_response=${sofaResponse.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        setCharityGifts(data);
      } catch (err) {
        console.error(err);
      }
    })();
  }, [caseId, sofaResponse]);

  const openAdd = () => {
    setNewCharity({
      charity_name: '',
      street1: '',
      street2: '',
      city: '',
      state: '',
      zip_code: '',
      gift_description: '',
      transaction_date: '',
      transaction_value: ''
    });
    setModalOpen(true);
  };

  const openEdit = charity => {
    const tx = (charity.transactions && charity.transactions[0]) || {};
    setEditCharity({
      ...charity,
      transaction_id: tx.id,
      transaction_date: tx.transaction_date || '',
      transaction_value: tx.transaction_value || ''
    });
    setEditModalOpen(true);
  };

  const saveNew = async () => {
    const res = await fetch(
      `${API_URL}/cases/${caseId}/sofa-charity-gifts/`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(newCharity)
      }
    );
    if (!res.ok) {
      console.error('Create failed', await res.text());
      return;
    }
    const created = await res.json();
    setCharityGifts(prev => [...prev, created]);
    setModalOpen(false);
  };

  const saveEdit = async () => {
    const res = await fetch(
      `${API_URL}/cases/${caseId}/sofa-charity-gifts/${editCharity.id}/`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(editCharity)
      }
    );
    if (!res.ok) {
      console.error('Update failed', await res.text());
      return;
    }
    const updated = await res.json();
    setCharityGifts(prev =>
      prev.map(c => {
        if (c.id === updated.id) {
          return {
            ...updated,
            transactions: [{
              id: editCharity.transaction_id,
              transaction_date: editCharity.transaction_date,
              transaction_value: editCharity.transaction_value
            }]
          };
        }
        return c;
      })
    );
    setEditModalOpen(false);
  };

  const deleteCharity = async id => {
    await fetch(
      `${API_URL}/cases/${caseId}/sofa-charity-gifts/${id}/`,
      { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
    );
    setCharityGifts(prev => prev.filter(c => c.id !== id));
  };

  return (
    <section className="p-6 bg-white shadow-lg rounded mb-6">
      <h2 className="text-xl font-semibold mb-4">Question 14: Charity Gifts</h2>
      <p className="text-sm text-gray-600 mb-4">
        List charitable contributions greater than $600 in the last 2 years.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
        {charityGifts.map(c => {
          const tx = c.transactions?.[0] || {};
          return (
            <div
              key={c.id}
              className="p-4 bg-white shadow rounded cursor-pointer hover:shadow-lg transition-shadow relative"
              onClick={() => openEdit(c)}
            >
              <button
                onClick={e => { e.stopPropagation(); deleteCharity(c.id); }}
                className="absolute top-2 right-2 text-red-600 hover:text-red-800"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" clipRule="evenodd" d="M6 2a1 1 0 00-1 1v1H3a1 1 0 000
                    2h14a1 1 0 000-2h-2V3a1 1 0 00-1-1H6zM4
                    6v10a2 2 0 002 2h8a2 2 0 002-2V6H4zm3
                    2a1 1 0 000 2v6a1 1 0 102 0V8a1 1 0 00-2
                    0zm4 0a1 1 0 000 2v6a1 1 0 102 0V8a1 1 0
                    00-2 0z"/>
                </svg>
              </button>
              <h3 className="font-medium text-lg">{c.charity_name}</h3>
              <p className="mt-1 text-sm">
                {c.street1}{c.street2 ? `, ${c.street2}` : ''}, {c.city}, {c.state} {c.zip_code}
              </p>
              {tx.transaction_value && (
                <p className="mt-2 text-sm"><strong>Amount:</strong> ${tx.transaction_value}</p>
              )}
              {tx.transaction_date && (
                <p className="mt-1 text-sm"><strong>Date:</strong> {tx.transaction_date}</p>
              )}
            </div>
          );
        })}
      </div>
      <button
        onClick={openAdd}
        className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
      >
        Add Charity Gift
      </button>

      {/* Add Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Add Charity Gift</h3>
            {/* Charity Name & Street fields */}
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Charity Name</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={newCharity.charity_name}
                onChange={e => setNewCharity(prev => ({ ...prev, charity_name: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 mb-2">
              <div>
                <label className="block text-sm font-medium mb-1">Street 1</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newCharity.street1}
                  onChange={e => setNewCharity(prev => ({ ...prev, street1: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Street 2</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newCharity.street2}
                  onChange={e => setNewCharity(prev => ({ ...prev, street2: e.target.value }))}
                />
              </div>
            </div>
            {/* City, State, Zip */}
            <div className="grid grid-cols-7 gap-4 mb-2">
              <div className="col-span-4">
                <label className="block text-sm font-medium mb-1">City</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newCharity.city}
                  onChange={e => setNewCharity(prev => ({ ...prev, city: e.target.value }))}
                />
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium mb-1">State</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newCharity.state}
                  onChange={e => setNewCharity(prev => ({ ...prev, state: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Zip Code</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newCharity.zip_code}
                  onChange={e => setNewCharity(prev => ({ ...prev, zip_code: e.target.value }))}
                />
              </div>
            </div>
            {/* Description */}
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                className="w-full border rounded px-2 py-1"
                rows="2"
                value={newCharity.gift_description}
                onChange={e => setNewCharity(prev => ({ ...prev, gift_description: e.target.value }))}
              />
            </div>
            {/* Value & Date */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Value</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full border rounded px-2 py-1"
                  value={newCharity.transaction_value}
                  onChange={e => setNewCharity(prev => ({ ...prev, transaction_value: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Date</label>
                <input
                  type="date"
                  className="w-full border rounded px-2 py-1"
                  value={newCharity.transaction_date}
                  onChange={e => setNewCharity(prev => ({ ...prev, transaction_date: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <button onClick={() => setModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
              <button onClick={saveNew} className="px-4 py-2 bg-blue-600 text-white rounded">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && editCharity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Edit Charity Gift</h3>
            {/* Charity Name & Street fields */}
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Charity Name</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={editCharity.charity_name}
                onChange={e => setEditCharity(prev => ({ ...prev, charity_name: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 mb-2">
              <div>
                <label className="block text-sm font-medium mb-1">Street 1</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editCharity.street1}
                  onChange={e => setEditCharity(prev => ({ ...prev, street1: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Street 2</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editCharity.street2}
                  onChange={e => setEditCharity(prev => ({ ...prev, street2: e.target.value }))}
                />
              </div>
            </div>
            {/* City, State, Zip */}
            <div className="grid grid-cols-7 gap-4 mb-2">
              <div className="col-span-4">
                <label className="block text-sm font-medium mb-1">City</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editCharity.city}
                  onChange={e => setEditCharity(prev => ({ ...prev, city: e.target.value }))}
                />
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium mb-1">State</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editCharity.state}
                  onChange={e => setEditCharity(prev => ({ ...prev, state: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Zip Code</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editCharity.zip_code}
                  onChange={e => setEditCharity(prev => ({ ...prev, zip_code: e.target.value }))}
                />
              </div>
            </div>
            {/* Description */}
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                className="w-full border rounded px-2 py-1"
                rows="2"
                value={editCharity.gift_description}
                onChange={e => setEditCharity(prev => ({ ...prev, gift_description: e.target.value }))}
              />
            </div>
            {/* Value & Date */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Value</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full border rounded px-2 py-1"
                  value={editCharity.transaction_value}
                  onChange={e => setEditCharity(prev => ({ ...prev, transaction_value: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Date</label>
                <input
                  type="date"
                  className="w-full border rounded px-2 py-1"
                  value={editCharity.transaction_date}
                  onChange={e => setEditCharity(prev => ({ ...prev, transaction_date: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <button onClick={() => setEditModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
              <button onClick={saveEdit} className="px-4 py-2 bg-blue-600 text-white rounded">Save</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}